/* eslint-disable prettier/prettier */
// tslint:disable:max-line-length
/**
 * @module use-cases/cart/slice
 * @desc Cart Slice (Reducers and ExtraReducers)
 *
 */

import type { Cart } from 'core/domains';
import { CART_SHIPPING_MODEL_VIEW, Utilities } from 'core/domains';
import { actions as checkoutActions } from 'core/use-cases/checkout/actions';
import { actions as actionsshippingModel } from 'core/use-cases/shippingModel';

import * as appActions from 'core/use-cases/app/slice/actions';
import { actions as comboActions } from 'core/use-cases/combo/slice';
import { createSlice, isAnyOf } from 'core/use-cases/utilities';

import { actions } from '../actions';

import {
  canSubmit,
  canSubmitShippingModel,
  cartData,
  isCartRunning,
  updateItemQuantity,
} from './helpers';
import { initialState } from './initial-state';
import { formatMoney } from 'core/utilities';

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(actions.cartResetState, () => initialState);
    builder.addCase(actions.cartCleanUp, () => initialState);

    builder.addCase(actions.itemAddFetch.failure, (state) => {
      if (!state.hasItem) {
        state.shippingModelHasBeenSelected = false;
      }
    });

    builder.addCase(checkoutActions.processFetch.success, (state, { payload }) => {
      const { items } = state.data;
      const updatedItems = items?.map((item) => {
        const foundItem = payload?.availableItems.find(
          (availableItem) => availableItem.sku === Number(item.productCode),
        );
        const updatedPrice =
          foundItem?.currentCycle[0].resellerPriceSpecification.price || item.priceNum;

        item.priceNum = updatedPrice;
        item.price = formatMoney(updatedPrice);
        item.priceTotalNum = updatedPrice * item?.quantity;
        item.priceTotal = formatMoney(updatedPrice * item?.quantity);
        return item;
      });

      state.current.code = payload?.order?.code;
      state.data.code = payload?.order?.code;
      state.data.items = updatedItems;
      state.data.cashbackNum = payload?.price?.cashback ?? 0;
      state.data.cashback = formatMoney(payload?.price?.cashback ?? 0);
      state.data.totalNum = payload?.price?.total ?? 0;
      state.data.total = formatMoney(payload?.price?.total ?? 0);
      state.data.subTotalNum = payload?.price?.subTotal ?? 0;
      state.data.subTotal = formatMoney(payload?.price?.subTotal ?? 0);
      state.data.shippingNum = payload?.price?.shipping ?? 0;
      state.data.shipping = formatMoney(payload?.price?.shipping ?? 0);
      state.data.totalDiscountNum = payload?.price?.discounts ?? 0;
      state.data.totalDiscount = formatMoney(payload?.price?.discounts ?? 0);

      state.current = {
        ...state.current,
        distributionCenterAddress: payload.shippingModel.address,
        shippingModel: {
          ...state.current.shippingModel,
          iswithdrawal: payload.shippingModel.isWithdrawalCenter,
          distributionCenterCode: payload.shippingModel.distributionCenterCode,
        },
      };
    });

    builder.addCase(actions.toggleDeleteConfirmation, (state, { payload: code }) => {
      if (!code) {
        return;
      }

      const { isOpen } = state.deleteConfirmation;

      if (isOpen) {
        state.deleteConfirmation.data = {} as Cart['deleteConfirmation']['data'];
        state.deleteConfirmation.isOpen = false;

        return;
      }

      const { data } = cartData(state, code);

      const dataItem = {
        ...(data
          ? {
              image: data.image,
              name: data.name,
              productCode: data.productCode,
              productPosition: data.productPosition,
              productOrigin: data.productOrigin,
            }
          : {}),
      } as Cart['deleteConfirmation']['data'];

      state.deleteConfirmation.data = dataItem;
      state.deleteConfirmation.isOpen = true;
    });

    builder.addCase(actions.setIsValidCoupon, (state, { payload }) => {
      state.data.coupon.isValidCoupon = payload;
    });

    builder.addCase(actions.toggleMinOrderConfirmation, (state) => {
      const { isOpen } = state.minOrderConfirmation;

      state.minOrderConfirmation.isOpen = !isOpen;
    });

    builder.addCase(actions.toggleComboEditConfirmation, (state, { payload = 0 }) => {
      const { isOpen } = state.comboEditConfirmation;

      state.comboEditConfirmation.isOpen = !isOpen;
      state.comboEditConfirmation.comboCode = payload;
    });

    builder.addCase(actions.cartFetchForGiftsCheck.request, (state) => {
      state.fetch.message = '';
      state.fetch.status = Utilities.STATUS.RUNNING;

      state.canSubmit = false;
      state.isRunning = isCartRunning(state);
    });

    builder.addCase(actions.cartFetchForGiftsCheck.success, (state, { meta, payload }) => {
      state.current.code = payload.code;
      state.data = payload;
      state.hasItem = !!payload.items.length;
      state.canAddItem = state.hasItem;
      state.fetch.message = '';
      state.fetch.status = Utilities.STATUS.SUCCESS;

      state.updatedAt = meta.updatedAt;
      state.canSubmit = canSubmit(state);
      state.isRunning = isCartRunning(state);
    });

    builder.addCase(actions.cartFetchForGiftsCheck.failure, (state, { error }) => {
      const { message } = error;

      state.fetch.message = message;
      state.fetch.status = Utilities.STATUS.ERROR;

      state.canSubmit = canSubmit(state);
      state.isRunning = isCartRunning(state);
    });

    builder.addCase(actions.cartSetShippingModelFetch.request, (state, { payload }) => {
      if (payload) {
        state.current.shippingModel = payload;
      }
    });

    builder.addCase(actions.cartSetShippingModelFetch.success, (state, { payload }) => {
      state.current.code = payload.code;
      state.data = payload;
      state.hasItem = !!payload.items.length;
      state.canAddItem = true;
      state.canSubmitShippingModel = canSubmitShippingModel(state);
    });

    builder.addCase(actions.cartSetShippingModelFetch.failure, (state) => {
      state.canAddItem = false;
      state.canSubmitShippingModel = canSubmitShippingModel(state);

      if (!state.hasItem) {
        state.shippingModelHasBeenSelected = false;
      }
    });

    builder.addCase(actionsshippingModel.currentDeliveryModeFetch.success, (state) => {
      state.canAddItem = true;
      state.canSubmitShippingModel = canSubmitShippingModel(state);
      state.shippingModelHasBeenSelected = true;
    });

    builder.addCase(actions.setBusinessModel, (state, { payload }) => {
      if (payload) {
        const modelCode = parseInt(
          payload.shippingModelType.toString().replace(/[^0-9,.]+/g, ''),
          10,
        );

        state.current.shippingModelType = payload.shippingModelType;

        state.current.shippingModel = {
          ...state.current.shippingModel,
          businessModelCode: modelCode,
          ...(payload.distributionCenterDefaultCode
            ? { distributionCenterCode: payload.distributionCenterDefaultCode }
            : {}),
        };
      }

      state.canSubmitShippingModel = canSubmitShippingModel(state);
    });

    builder.addCase(actions.setDefaultShippingModel, (state, { payload = {} }) => {
      const { code, delivery } = payload;
      state.canAddItem = false;
      state.step = initialState.step;
      state.canSubmitShippingModel = true;

      state.current.distributionCenterAddress = delivery?.address || '';
      state.current.shippingModel = {
        businessModelCode: code,
        changeDeliveryMode: true,
        distributionCenterCode: delivery?.distributionCenterCode,
        iswithdrawal: delivery?.isWithdrawalCenter || false,
      };
    });

    builder.addCase(actions.setDistributionCenter, (state, { payload }) => {
      if (payload) {
        state.current.shippingModel = {
          ...state.current.shippingModel,
          distributionCenterCode: payload.code,
          iswithdrawal: true,
        };
        state.current.distributionCenterAddress = payload.address;
      }

      state.canSubmitShippingModel = canSubmitShippingModel(state);
    });

    builder.addCase(actions.setShippingModelStep, (state, { payload }) => {
      if (
        !payload?.defaultDistributionCenterCode &&
        !payload?.defaultShippingModelCode &&
        payload?.step === CART_SHIPPING_MODEL_VIEW.OPTIONS
      ) {
        return;
      }

      if (payload) {
        state.step = payload.step;
        if (payload.step === CART_SHIPPING_MODEL_VIEW.SELECT_PICKUP_STORE) {
          state.current.shippingModel = {
            ...state.current.shippingModel,
            distributionCenterCode: undefined,
            iswithdrawal: true,
          };
        } else {
          state.current.shippingModel = {
            ...state.current.shippingModel,
            businessModelCode: payload.defaultShippingModelCode,
            distributionCenterCode: payload.defaultDistributionCenterCode,
            iswithdrawal: false,
          };
        }
      }
      state.canSubmitShippingModel = canSubmitShippingModel(state);
    });

    builder.addCase(checkoutActions.setBankSlipOption, (state, { payload = '' }) => {
      state.data.paymentOption = payload;
    });

    builder.addMatcher(
      isAnyOf(
        actions.cartFetch.request,
        actions.lastCartFetch.request,
        actions.cartCurrentDeliveryFetch.request,
      ),
      (state, action) => {
        state.fetch.message = '';
        state.fetch.status = Utilities.STATUS.RUNNING;

        state.canSubmit = false;
        state.isRunning = isCartRunning(state);

        if (action.type === actions.lastCartFetch.request.type) {
          state.hasBeenCalled = false;
          state.shippingModelHasBeenSelected = false;
        }
      },
    );

    builder.addMatcher(
      isAnyOf(
        actions.cartFetch.success,
        actions.lastCartFetch.success,
        actions.cartCurrentDeliveryFetch.success,
      ),
      (state, { meta, payload, type }) => {
        state.current.code = payload.code;
        state.data = payload;
        state.hasItem = !!payload.items.length;
        state.canAddItem = state.hasItem;
        state.fetch.message = '';
        state.fetch.status = Utilities.STATUS.SUCCESS;

        state.updatedAt = meta.updatedAt;
        state.canSubmit = canSubmit(state);
        state.isRunning = isCartRunning(state);
        if (
          type === actions.cartFetch.success.type ||
          type === actions.cartCurrentDeliveryFetch.success.type
        ) {
          state.hasBeenCalled = true;
          state.shippingModelNotFound = false;
        }
      },
    );

    builder.addMatcher(
      isAnyOf(
        actions.cartFetch.failure,
        actions.lastCartFetch.failure,
        actions.cartCurrentDeliveryFetch.failure,
      ),
      (state, { error, type }) => {
        const { message } = error;

        state.fetch.message = message;
        state.fetch.status = Utilities.STATUS.ERROR;

        state.canSubmit = canSubmit(state);
        state.isRunning = isCartRunning(state);
        if (
          type === actions.cartFetch.failure.type ||
          type === actions.cartCurrentDeliveryFetch.failure.type
        ) {
          state.hasBeenCalled = false;
          state.shippingModelNotFound = true;
        }

        if (!state.hasItem) {
          state.shippingModelHasBeenSelected = false;
        }
      },
    );

    builder.addMatcher(
      isAnyOf(actions.setCouponFetch.request, actions.clearCouponFetch.request),
      (state, { payload }) => {
        state.couponUpdate.message = '';
        state.data.coupon.isHiddenButtonError = false;
        state.data.coupon.isHiddenButtonSuccess = false;
        state.data.coupon.isHiddenButtonDeleteSuccess = false;
        state.couponUpdate.meta = {
          couponNumber: payload?.couponNumber || null,
        };
        state.couponUpdate.status = Utilities.STATUS.RUNNING;
      },
    );

    builder.addMatcher(
      isAnyOf(actions.setCouponFetch.success, actions.clearCouponFetch.success),
      (state, { meta, payload, type }) => {
        state.current.code = payload.code;
        state.data = payload;
        state.updatedAt = meta.updatedAt;
        state.data.coupon.couponNumber = state.couponUpdate.meta.couponNumber;

        state.couponUpdate.status = Utilities.STATUS.SUCCESS;
        state.couponUpdate.updatedAt = meta.updatedAt;

        if (type === actions.setCouponFetch.success.type) {
          state.data.coupon.isValidCoupon = true;
          state.data.coupon.isHiddenButtonSuccess = true;
        }

        if (type === actions.clearCouponFetch.success.type) {
          state.data.coupon.isValidCoupon = null;
          state.data.coupon.isHiddenButtonDeleteSuccess = true;
        }

        state.canSubmit = canSubmit(state);
      },
    );

    builder.addMatcher(
      isAnyOf(actions.setCouponFetch.failure, actions.clearCouponFetch.failure),
      (state, { error, type }) => {
        const { message } = error;

        state.couponUpdate.message = message;
        state.couponUpdate.status = Utilities.STATUS.ERROR;

        if (type === actions.setCouponFetch.failure.type) {
          state.data.coupon.isValidCoupon = false;
          state.data.coupon.isHiddenButtonError = true;
        }

        state.canSubmit = canSubmit(state);
      },
    );

    builder.addMatcher(
      isAnyOf(
        actions.itemAddFetch.request,
        actions.itemUpdateFetch.request,
        actions.itemDeleteFetch.request,
      ),
      (state, action) => {
        state.itemUpdate.message = '';
        state.itemUpdate.meta = action.payload!;
        state.itemUpdate.status = Utilities.STATUS.RUNNING;

        updateItemQuantity(state, action);

        state.canSubmit = canSubmit(state);
        state.isRunning = isCartRunning(state);
      },
    );

    builder.addMatcher(
      isAnyOf(
        actions.itemAddFetch.success,
        actions.itemUpdateFetch.success,
        actions.itemDeleteFetch.success,
        comboActions.comboPost.success,
      ),
      (state, action) => {
        state.data = action.payload;
        state.current.code = action.payload.code;
        state.hasItem = !!action.payload.items.length;
        state.itemUpdate.message = '';
        state.itemUpdate.meta = {} as Cart['itemUpdate']['meta'];
        state.itemUpdate.status = Utilities.STATUS.SUCCESS;
        state.canAddItem = state.hasItem;
        state.updatedAt = action.meta.updatedAt;
        state.canSubmit = canSubmit(state);
        state.isRunning = isCartRunning(state);

        if (action.type !== actions.itemAddFetch.success.type) {
          state.shippingModelHasBeenSelected = state.hasItem;
        }
      },
    );

    builder.addMatcher(
      isAnyOf(
        actions.itemAddFetch.failure,
        actions.itemUpdateFetch.failure,
        actions.itemDeleteFetch.failure,
      ),
      (state, action) => {
        if (
          action.type === actions.itemDeleteFetch.failure.type &&
          action.meta?.response?.success === false
        ) {
          state.data = action.meta?.response;
          state.current.code = action.meta?.response.code;
          state.hasItem = !!action.meta?.response.items.length;
          state.canAddItem = !!action.meta?.response.items.length;
        } else {
          updateItemQuantity(state, action);
          state.canAddItem = state.hasItem;
        }
        state.itemUpdate.status = Utilities.STATUS.ERROR;
        state.itemUpdate.message = action.error.message;
        state.canSubmit = canSubmit(state);
        state.isRunning = isCartRunning(state);
      },
    );

    builder.addMatcher(isAnyOf(comboActions.comboRedirect, appActions.toggleCart), (state) => {
      state.comboEditConfirmation.isOpen = false;
    });
  },
});

export const { reducer } = cartSlice;
